<script setup lang="ts">
import { computed } from 'vue';
import BCard from './BCard.vue';
import BIcon from './BIcon.vue';

type PropsType = {
    modelValue: boolean;
    icon: string;
    title: string;
    subtitle?: string;
    disabled?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), { disabled: false });

const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    click: [];
}>();

const cardClass = computed(() => {
    const classes = ['b-select-card'];
    if (props.modelValue) {
        classes.push('b-select-card--selected');
    }
    if (props.disabled) {
        classes.push('b-select-card--disabled');
    }

    return classes.join(' ');
});

const handleClick = () => {
    if (props.disabled) {
        return;
    }
    emit('update:modelValue', !props.modelValue);
    emit('click');
};
</script>

<template>
    <BCard
        variant="outlined"
        :class="cardClass"
        @click="handleClick"
    >
        <div class="b-select-card__content d-flex flex-column align-center text-center">
            <div class="b-select-card__icon-wrapper">
                <BIcon
                    :icon="icon"
                    size="x-large"
                    class="b-select-card__icon"
                />
            </div>
            <div class="b-select-card__title text-h5 font-weight-bold mt-5">
                {{ title }}
            </div>
            <div
                v-if="subtitle"
                class="b-select-card__subtitle text-body-1 text-grey-600 mt-2"
            >
                {{ subtitle }}
            </div>
        </div>
    </BCard>
</template>

<style scoped lang="scss">
@keyframes icon-bounce {
    0%,
    100% {
        transform: translateY(0) scale(1);
    }
    25% {
        transform: translateY(-6px) scale(1.1);
    }
    50% {
        transform: translateY(-3px) scale(1.05);
    }
    75% {
        transform: translateY(-5px) scale(1.08);
    }
}

@keyframes icon-wiggle {
    0%,
    100% {
        transform: rotate(0deg);
    }
    25% {
        transform: rotate(-8deg);
    }
    75% {
        transform: rotate(8deg);
    }
}

.b-select-card {
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        background-color 0.2s ease;
    border: 1px solid rgb(var(--v-theme-grey-200));
    background-color: transparent;
    border-radius: 8px;
    cursor: pointer;

    &__content {
        padding: 40px 25px;
    }

    &__icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 12px;
        background-color: rgba(var(--v-theme-primary), 0.08);
        transition: background-color 0.2s ease;
    }

    &__icon {
        color: rgb(var(--v-theme-primary));
        transition: transform 0.3s ease;
    }

    &__title {
        color: rgba(var(--v-theme-on-surface), 0.87);
        transition: color 0.2s ease;
    }

    &:hover:not(&--disabled):not(&--selected) {
        border-color: rgb(var(--v-theme-primary));
        box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.15);
        background-color: rgba(var(--v-theme-primary), 0.1);

        .b-select-card__icon-wrapper {
            background-color: rgba(var(--v-theme-primary), 0.12);
        }

        .b-select-card__icon {
            animation: icon-wiggle 0.5s ease-in-out;
        }
    }

    &--selected {
        border: 2px solid rgb(var(--v-theme-primary));
        background-color: rgba(var(--v-theme-primary), 0.08);
        box-shadow: 0 4px 20px rgba(var(--v-theme-primary), 0.2);

        .b-select-card__icon-wrapper {
            background-color: rgba(var(--v-theme-primary), 0.15);
        }

        .b-select-card__icon {
            animation: icon-bounce 0.6s ease-out;
        }

        .b-select-card__title {
            color: rgb(var(--v-theme-primary));
        }
    }

    &--disabled {
        cursor: not-allowed;
        opacity: 0.6;

        .b-select-card__icon {
            color: rgb(var(--v-theme-grey-400));
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .b-select-card {
        transition: none;

        &__icon {
            transition: none;
            animation: none !important;
        }

        &__icon-wrapper {
            transition: none;
        }

        &__title {
            transition: none;
        }
    }
}
</style>
